// ── Design decisions ──
// Fila de artículo de la agencia, pensada para el mostrador: la operadora
// teclea rápido y odia el mouse, así que la fila entera se recorre con Enter.
//
// · Orden fijo Cant → Descripción → Nuevo/Usado → Valor unit. → Total → acciones.
//   Enter avanza Cant → Descripción → Valor y SALTA el control de condición
//   (Tab sí lo alcanza; ahí N y U fijan Nuevo y Usado). Enter en Valor guarda
//   la fila y le pide al padre una fila nueva enfocada en Cant.
// · La fila se dimensiona contra su TARJETA (container query en
//   AgencyEncomienda.css): una línea en tarjeta ancha (≥ 720 px), dos líneas
//   en tarjeta angosta (Cant + Descripción / Condición · Valor · Total ·
//   acciones). El DOM va en ese mismo orden, así que el orden visual y el de
//   foco coinciden en ambos modos.
// · Sugerencias del catálogo debajo de la descripción (listbox): ↑/↓ resaltan,
//   Enter liga la resaltada y pasa a Valor, Esc cierra la lista. Nunca se liga
//   sola: la operadora siempre confirma.
// · Fila ligada al catálogo: la descripción es texto de SOLO LECTURA que
//   envuelve hasta 2 líneas (no un input de una línea), enfocable
//   (role="textbox" aria-readonly) para que Enter siga saltando a Valor; debajo
//   la línea "Cliente: {description_raw}" y Desligar. Sin ligar, el input
//   editable lleva el texto completo en `title`. Si el catálogo cotiza por
//   KILOGRAMO y el artículo está en LB, se avisa en ámbar; nunca se convierte.
// · Los chips de origen y revisión viven en la línea meta bajo la descripción
//   (la columna flexible), no en las acciones: así mover y borrar nunca se
//   salen de la tarjeta.
// · Mover: botón compacto (ArrowRightLeft, "Mover a otro paquete") que abre un
//   menú con los otros paquetes y "Nuevo paquete"; Alt+↓ sigue moviendo al
//   siguiente sin abrir nada. Borrar: dos toques en línea sin modal — el
//   primero pone el botón en rojo con "¿Eliminar?" por 3 s (y esconde Mover
//   para no crecer), el segundo borra. Alt+Supr sobre la fila hace lo mismo.
// · Una fila vacía (sin descripción ni valor) jamás dispara el guardado; Esc
//   sobre ella la quita. El guardado es silencioso: solo un "guardado" chiquito.
// · Un total de $0.00 va en gris neutro, no en verde.
// · onBlur(patch?) acepta un parche opcional para que los cambios inmediatos
//   (condición, revisado) se guarden con el valor nuevo y no con el render viejo.
// · No se reutiliza AnexoItemRow: su contrato de ítem (descripcion /
//   fob_unitario_usd / cantidad) y su flujo (Enter en Descripción crea fila,
//   catálogo elegido salta a Cantidad) no se traducen limpio al orden Cant →
//   Descripción → Valor que pidió la agencia. Se replica su comportamiento
//   de teclado con el contrato de ítem de la agencia.

import { useEffect, useRef, useState } from 'react';
import { ArrowRightLeft, Trash2, Check, Link2Off } from 'lucide-react';
import { ProvenanceChip, ReviewDot } from '../ViajeroEncomienda/shared/chips';
import { MAX_DESC } from './agencyMapper';

const DEBOUNCE_MS = 250;
const MAX_SUGERENCIAS = 5;
const CONFIRMAR_MS = 3000;

const chipBase =
  'inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-semibold leading-4';

const FuenteChip = ({ source }) => {
  if (source === 'ai') return <ProvenanceChip source="ai" />;
  if (source === 'texto') {
    return (
      <span className={`${chipBase} border-sky-200 bg-sky-50 text-sky-700`} title="Leído del texto pegado">
        Texto
      </span>
    );
  }
  if (source === 'excel') {
    return (
      <span className={`${chipBase} border-emerald-200 bg-emerald-50 text-emerald-700`} title="Leído del archivo Excel">
        Excel
      </span>
    );
  }
  return null;
};

const AgencyItemRow = ({
  item,
  unitType = 'POR_UNIDAD',
  otrosPaquetes = [],
  siguientePaqueteId = null,
  autoFocus = null,
  saveState = null,
  onChange,
  onBlur,
  onEnterAtValor,
  onLinkCatalog,
  onUnlinkCatalog,
  onDelete,
  onMove,
  onEscapeEmpty,
  buscarCatalogo,
}) => {
  const cantRef = useRef(null);
  const descRef = useRef(null);
  const valorRef = useRef(null);
  const nuevoRef = useRef(null);
  const usadoRef = useRef(null);
  const moverRef = useRef(null);
  const menuRef = useRef(null);
  const escribiendoRef = useRef(false);
  const saltarBlurRef = useRef(false);
  const solicitud = useRef(0);

  const [sugerencias, setSugerencias] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [resaltado, setResaltado] = useState(0);
  const [enfocadoDesc, setEnfocadoDesc] = useState(false);
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);
  const [menuAbierto, setMenuAbierto] = useState(false);

  const ligado = Boolean(item.catalog_id);
  const esPeso = unitType === 'POR_PESO';
  const desc = String(item.description ?? '');
  const valorNum = parseFloat(item.unitValue);
  const esVacio = desc.trim() === '' && !(valorNum > 0);
  const total = (parseFloat(item.quantity) || 0) * (parseFloat(item.unitValue) || 0);
  const siguienteId = siguientePaqueteId ?? otrosPaquetes[0]?.id ?? null;

  useEffect(() => {
    if (autoFocus === 'cant') cantRef.current?.focus();
    else if (autoFocus === 'desc') descRef.current?.focus();
    else if (autoFocus === 'valor') valorRef.current?.focus();
  }, [autoFocus]);

  // Borrado armado: se desarma solo a los 3 s si la operadora sigue trabajando.
  useEffect(() => {
    if (!confirmarBorrar) return undefined;
    const t = setTimeout(() => setConfirmarBorrar(false), CONFIRMAR_MS);
    return () => clearTimeout(t);
  }, [confirmarBorrar]);

  // Menú "Mover a otro paquete": clic afuera lo cierra.
  useEffect(() => {
    if (!menuAbierto) return undefined;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) && !moverRef.current?.contains(e.target)) {
        setMenuAbierto(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuAbierto]);

  useEffect(() => {
    if (menuAbierto) menuRef.current?.querySelector('[role="menuitem"]')?.focus();
  }, [menuAbierto]);

  // Sugerencias del catálogo: solo mientras la operadora escribe y la fila no
  // está ligada. La última respuesta gana.
  useEffect(() => {
    if (!escribiendoRef.current || !enfocadoDesc || ligado || typeof buscarCatalogo !== 'function') {
      return undefined;
    }
    const q = desc.trim();
    if (q.length < 2) {
      setSugerencias([]);
      setAbierto(false);
      return undefined;
    }
    const id = ++solicitud.current;
    const t = setTimeout(async () => {
      try {
        const filas = await buscarCatalogo(q);
        if (id !== solicitud.current) return;
        const lista = (Array.isArray(filas) ? filas : []).slice(0, MAX_SUGERENCIAS);
        setSugerencias(lista);
        setResaltado(0);
        setAbierto(lista.length > 0);
      } catch (_) {
        if (id !== solicitud.current) return;
        setSugerencias([]);
        setAbierto(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [desc, enfocadoDesc, ligado, buscarCatalogo]);

  const guardar = (patch) => {
    if (esVacio && !item.id && !patch) return;
    onBlur && onBlur(patch);
  };

  const editar = (patch) => {
    onChange && onChange(item.source && item.source !== 'manual' ? { ...patch, source: 'manual' } : patch);
  };

  const enfocarSinGuardar = (ref) => {
    saltarBlurRef.current = true;
    ref.current?.focus();
  };

  const onBlurCampo = () => {
    if (saltarBlurRef.current) {
      saltarBlurRef.current = false;
      return;
    }
    guardar();
  };

  const elegirSugerencia = (row) => {
    escribiendoRef.current = false;
    setAbierto(false);
    setSugerencias([]);
    onLinkCatalog && onLinkCatalog(row);
    enfocarSinGuardar(valorRef);
  };

  const fijarCondicion = (condition) => {
    if (item.condition === condition) return;
    editar({ condition });
    guardar({ condition });
  };

  const moverSiguiente = () => {
    if (siguienteId && onMove) onMove(siguienteId);
  };

  const moverA = (target) => {
    setMenuAbierto(false);
    moverRef.current?.focus();
    if (target && onMove) onMove(target);
  };

  // Dos toques: armar → confirmar. Alt+Supr y el botón comparten esta ruta.
  const tocarBorrar = () => {
    if (confirmarBorrar) {
      setConfirmarBorrar(false);
      onDelete && onDelete();
    } else {
      setConfirmarBorrar(true);
    }
  };

  const onKeyDownFila = (e) => {
    if (e.altKey && e.key === 'ArrowDown') {
      e.preventDefault();
      moverSiguiente();
    } else if (e.altKey && e.key === 'Delete') {
      e.preventDefault();
      tocarBorrar();
    }
  };

  const onKeyDownCant = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      enfocarSinGuardar(descRef);
    } else if (e.key === 'Escape' && esVacio && !item.id) {
      e.preventDefault();
      onEscapeEmpty && onEscapeEmpty();
    }
  };

  const onKeyDownDesc = (e) => {
    if (abierto && sugerencias.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setResaltado((i) => (i + 1) % sugerencias.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setResaltado((i) => (i - 1 + sugerencias.length) % sugerencias.length);
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        elegirSugerencia(sugerencias[resaltado]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setAbierto(false);
        return;
      }
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      enfocarSinGuardar(valorRef);
    } else if (e.key === 'Escape' && esVacio && !item.id) {
      e.preventDefault();
      onEscapeEmpty && onEscapeEmpty();
    }
  };

  const onKeyDownValor = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saltarBlurRef.current = true;
      guardar();
      onEnterAtValor && onEnterAtValor();
    } else if (e.key === 'Escape' && esVacio && !item.id) {
      e.preventDefault();
      onEscapeEmpty && onEscapeEmpty();
    }
  };

  const onKeyDownCondicion = (e) => {
    const k = e.key.toLowerCase();
    if (k === 'n') {
      e.preventDefault();
      fijarCondicion('NUEVO');
      nuevoRef.current?.focus();
    } else if (k === 'u') {
      e.preventDefault();
      fijarCondicion('USADO');
      usadoRef.current?.focus();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      const next = item.condition === 'USADO' ? 'NUEVO' : 'USADO';
      fijarCondicion(next);
      (next === 'USADO' ? usadoRef : nuevoRef).current?.focus();
    }
  };

  const onKeyDownMenu = (e) => {
    const items = Array.from(menuRef.current?.querySelectorAll('[role="menuitem"]') || []);
    const i = items.indexOf(document.activeElement);
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setMenuAbierto(false);
      moverRef.current?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      items[(i + 1) % items.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      items[(i - 1 + items.length) % items.length]?.focus();
    }
  };

  const onBlurDesc = () => {
    setTimeout(() => {
      setEnfocadoDesc(false);
      setAbierto(false);
    }, 150);
    onBlurCampo();
  };

  const inputCls = 'input focus-visible:ring-2 focus-visible:ring-[var(--blue)]';
  const avisoKg = ligado && item.catalogo_unidad_medida === 'KILOGRAMO' && item.unidad_medida === 'LB';
  const hayMeta = Boolean(item.source && item.source !== 'manual') || Boolean(item.pending_review);

  return (
    <div
      role="group"
      aria-label={`Artículo ${desc || 'nuevo'}`}
      onKeyDown={onKeyDownFila}
      className={`agency-row${item.pending_review ? ' agency-row--revisar' : ''}`}
    >
      {/* Cant */}
      <div className="agency-row__cant">
        <p className="agency-row__label metric-label">Cant</p>
        <input
          ref={cantRef}
          type="number"
          min={esPeso ? 0 : 1}
          step={esPeso ? 0.01 : 1}
          inputMode="decimal"
          enterKeyHint="next"
          aria-label="Cantidad"
          className={inputCls}
          value={item.quantity ?? ''}
          onChange={(e) => editar({ quantity: e.target.value })}
          onKeyDown={onKeyDownCant}
          onBlur={onBlurCampo}
        />
        {esPeso && <p className="metric-sub text-center mt-1">{item.unidad_medida || 'LB'}</p>}
      </div>

      {/* Descripción */}
      <div className="agency-row__desc">
        <p className="agency-row__label metric-label">Descripción</p>
        {ligado ? (
          <div
            ref={descRef}
            role="textbox"
            aria-readonly="true"
            tabIndex={0}
            aria-label="Descripción del artículo"
            title={desc}
            className="agency-row__desc-ligada"
            onKeyDown={onKeyDownDesc}
            onFocus={() => setEnfocadoDesc(true)}
            onBlur={onBlurDesc}
          >
            {desc}
          </div>
        ) : (
          <input
            ref={descRef}
            type="text"
            maxLength={MAX_DESC}
            enterKeyHint="next"
            aria-label="Descripción del artículo"
            placeholder="Descripción del artículo"
            title={desc || undefined}
            autoComplete="off"
            role={abierto && sugerencias.length > 0 ? 'combobox' : undefined}
            aria-expanded={abierto && sugerencias.length > 0 ? true : undefined}
            aria-controls={abierto && sugerencias.length > 0 ? `sug-${item.tempId}` : undefined}
            className={inputCls}
            value={desc}
            onChange={(e) => {
              escribiendoRef.current = true;
              editar({ description: e.target.value.slice(0, MAX_DESC) });
            }}
            onKeyDown={onKeyDownDesc}
            onFocus={() => setEnfocadoDesc(true)}
            onBlur={onBlurDesc}
          />
        )}
        {ligado && (
          <div className="agency-row__meta">
            <span className="metric-sub" style={{ marginTop: 0 }}>
              Cliente: {item.description_raw || '—'}
            </span>
            <button
              type="button"
              onClick={() => onUnlinkCatalog && onUnlinkCatalog()}
              aria-label="Desligar del catálogo"
              title="Desligar del catálogo: vuelve al texto del cliente"
              className="btn btn-ghost focus-visible:ring-2 focus-visible:ring-[var(--blue)]"
              style={{ padding: '2px 6px', fontSize: '10px', color: 'var(--blue-text)' }}
            >
              <Link2Off size={11} aria-hidden="true" /> Desligar
            </button>
          </div>
        )}
        {hayMeta && (
          <div className="agency-row__meta">
            <FuenteChip source={item.source} />
            <ReviewDot visible={Boolean(item.pending_review)} />
            {item.pending_review && (
              <button
                type="button"
                onClick={() => {
                  editar({ pending_review: false });
                  guardar({ pending_review: false });
                }}
                aria-label="Marcar como revisado"
                title="Marcar como revisado"
                className="badge badge-hold focus-visible:ring-2 focus-visible:ring-[var(--amber)]"
                style={{ cursor: 'pointer', border: '1px solid var(--amber)' }}
              >
                <Check size={9} aria-hidden="true" className="inline mr-0.5" /> Revisado
              </button>
            )}
          </div>
        )}
        {avisoKg && (
          <p className="badge badge-hold mt-1" style={{ whiteSpace: 'normal' }} role="status">
            Precio del catálogo es por kilogramo — confirmá la unidad.
          </p>
        )}
        {!ligado && abierto && enfocadoDesc && sugerencias.length > 0 && (
          <ul
            id={`sug-${item.tempId}`}
            role="listbox"
            aria-label="Sugerencias del catálogo"
            className="tile absolute left-0 right-0 z-30 mt-1"
            style={{ padding: 0, maxHeight: '220px', overflowY: 'auto' }}
          >
            {sugerencias.map((row, i) => (
              <li
                key={row.id}
                role="option"
                aria-selected={i === resaltado}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => elegirSugerencia(row)}
                onMouseEnter={() => setResaltado(i)}
                className="row-item cursor-pointer"
                style={{
                  padding: '8px 12px',
                  fontSize: '12px',
                  background: i === resaltado ? 'var(--bg)' : 'transparent',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <span className="truncate" style={{ flex: 1, minWidth: 0 }}>{row.descripcion}</span>
                <span className="metric-sub" style={{ marginTop: 0, whiteSpace: 'nowrap' }}>
                  {row.unidad_medida || 'UNIDAD'}
                  {row.fob_usd != null ? ` · $${Number(row.fob_usd).toFixed(2)}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Nuevo / Usado */}
      <div className="agency-row__cond">
        <p className="agency-row__label metric-label">Condición</p>
        <div role="radiogroup" aria-label="Condición del artículo" className="agency-row__cond-grupo" onKeyDown={onKeyDownCondicion}>
          <button
            ref={nuevoRef}
            type="button"
            role="radio"
            aria-checked={item.condition !== 'USADO'}
            tabIndex={item.condition !== 'USADO' ? 0 : -1}
            title="Nuevo (tecla N)"
            onClick={() => fijarCondicion('NUEVO')}
            className={`btn ${item.condition !== 'USADO' ? 'btn-primary' : 'btn-secondary'} focus-visible:ring-2 focus-visible:ring-[var(--blue)]`}
          >
            Nuevo
          </button>
          <button
            ref={usadoRef}
            type="button"
            role="radio"
            aria-checked={item.condition === 'USADO'}
            tabIndex={item.condition === 'USADO' ? 0 : -1}
            title="Usado (tecla U)"
            onClick={() => fijarCondicion('USADO')}
            className="btn btn-secondary focus-visible:ring-2 focus-visible:ring-[var(--blue)]"
            style={{
              background: item.condition === 'USADO' ? 'var(--text-secondary)' : '',
              color: item.condition === 'USADO' ? '#fff' : '',
            }}
          >
            Usado
          </button>
        </div>
      </div>

      {/* Valor unitario */}
      <div className="agency-row__valor">
        <p className="agency-row__label metric-label">Valor unit. ($)</p>
        <input
          ref={valorRef}
          type="number"
          min={0}
          step={0.01}
          inputMode="decimal"
          enterKeyHint="done"
          aria-label="Valor unitario"
          placeholder="0.00"
          className={inputCls}
          value={item.unitValue ?? ''}
          onChange={(e) => editar({ unitValue: e.target.value, pending_review: item.pending_review && !(parseFloat(e.target.value) > 0) })}
          onKeyDown={onKeyDownValor}
          onBlur={onBlurCampo}
        />
      </div>

      {/* Total */}
      <div className="agency-row__total">
        <p className="agency-row__label metric-label">Total</p>
        <div
          className="font-mono font-bold text-sm whitespace-nowrap"
          style={{ color: total > 0 ? 'var(--green)' : 'var(--text-tertiary)', paddingTop: '6px' }}
          aria-label={`Total del artículo: ${total.toFixed(2)} dólares`}
        >
          ${total.toFixed(2)}
        </div>
        <div aria-live="polite">
          {saveState === 'saving' && <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>guardando…</span>}
          {saveState === 'saved' && <span className="text-[10px]" style={{ color: 'var(--green)' }}>guardado</span>}
        </div>
      </div>

      {/* Acciones: mover + borrar, siempre dentro de la tarjeta */}
      <div className="agency-row__acciones">
        {!confirmarBorrar && (
          <div className="agency-row__mover">
            <button
              ref={moverRef}
              type="button"
              onClick={() => setMenuAbierto((v) => !v)}
              aria-label="Mover a otro paquete"
              aria-haspopup="menu"
              aria-expanded={menuAbierto}
              title="Mover a otro paquete (Alt+↓ mueve al siguiente)"
              className="agency-row__btn focus-visible:ring-2 focus-visible:ring-[var(--blue)]"
            >
              <ArrowRightLeft size={14} aria-hidden="true" />
            </button>
            {menuAbierto && (
              <ul ref={menuRef} role="menu" aria-label="Mover a otro paquete" className="agency-row__menu tile" onKeyDown={onKeyDownMenu}>
                {otrosPaquetes.map((p) => (
                  <li key={p.id} role="none">
                    <button type="button" role="menuitem" onClick={() => moverA(p.id)}>
                      {p.label}
                    </button>
                  </li>
                ))}
                <li role="none">
                  <button type="button" role="menuitem" onClick={() => moverA('nuevo')}>
                    Nuevo paquete
                  </button>
                </li>
              </ul>
            )}
          </div>
        )}
        <button
          type="button"
          onClick={tocarBorrar}
          aria-label={confirmarBorrar ? 'Confirmar eliminación del artículo' : 'Eliminar artículo'}
          title={confirmarBorrar ? 'Tocá otra vez para eliminar (Alt+Supr)' : 'Eliminar artículo (Alt+Supr)'}
          className={`agency-row__btn agency-row__btn--peligro${confirmarBorrar ? ' agency-row__btn--armado' : ''} focus-visible:ring-2 focus-visible:ring-[var(--avianca-red)]`}
        >
          <Trash2 size={13} aria-hidden="true" />
          {confirmarBorrar && <span>¿Eliminar?</span>}
        </button>
      </div>
    </div>
  );
};

export default AgencyItemRow;
